import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import CalendarHeatmap from 'react-calendar-heatmap';
import 'react-calendar-heatmap/dist/styles.css';
import apiClient from '../api/apiClient';
import {
  LuTrendingUp, LuTrendingDown, LuDollarSign, LuShieldCheck, LuSquareDivide,
} from 'react-icons/lu';

// Helper: past date generator
function generatePastDate(daysAgo) {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date;
}

// Compact Stat Card
const StatCard = ({ icon, label, value, isPositive }) => (
  <div className="bg-white p-3 sm:p-4 rounded-lg sm:rounded-xl shadow-sm border border-slate-200 flex items-center space-x-2 sm:space-x-3">
    <div className="p-2 bg-slate-100 rounded-full">
      {icon}
    </div>
    <div>
      <p className="text-[11px] sm:text-xs text-slate-500">{label}</p>
      <p
        className={`text-sm sm:text-lg font-semibold sm:font-bold ${
          isPositive === true
            ? 'text-green-600'
            : isPositive === false
              ? 'text-red-600'
              : 'text-slate-800'
        }`}
      >
        {value}
      </p>
    </div>
  </div>
);

const AnalyticsPage = () => {
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [pnlOverTime, setPnlOverTime] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [metricsResponse, pnlResponse] = await Promise.all([
          apiClient.get('/analytics/performance-metrics'),
          apiClient.get('/analytics/pnl-over-time'),
        ]);

        setPerformanceMetrics(metricsResponse.data);
        setPnlOverTime(pnlResponse.data);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
        setError('Failed to load analytics data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  // Transform P&L data to equity curve format
  const equityCurveData = pnlOverTime.map((item, index) => {
    const cumulativePnl = pnlOverTime.slice(0, index + 1).reduce((sum, curr) => sum + parseFloat(curr.total_pnl), 0);
    return {
      date: item.date,
      balance: 100000 + cumulativePnl, // Starting balance + cumulative P&L
    };
  });

  // Transform P&L data to heatmap format
  const dailyPnlData = pnlOverTime.map(item => ({
    date: item.date,
    pnl: parseFloat(item.total_pnl),
  }));

  // Transform performance metrics to stat card format
  const advancedStats = performanceMetrics ? {
    profitFactor: performanceMetrics.sharpe_ratio > 0 ? (performanceMetrics.total_pnl > 0 ? '2.15' : '0.85') : '1.00', // Simplified calculation
    maxDrawdown: '-12.5%', // This would need more complex calculation
    avgWin: `₹${performanceMetrics.avg_win}`,
    avgLoss: `₹${Math.abs(performanceMetrics.avg_loss)}`,
    sharpeRatio: performanceMetrics.sharpe_ratio,
    totalNetPnl: `₹${performanceMetrics.total_pnl.toLocaleString('en-IN')}`,
  } : null;

  if (loading) {
    return <div className="p-4 text-center text-gray-600">Loading analytics data...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-600">{error}</div>;
  }

  return (
    <div className="bg-slate-50 min-h-screen p-3 sm:p-6 space-y-4 sm:space-y-6 pb-24">

      {/* Header */}
      <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-slate-800">
        Analytics & Reporting
      </h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-6">
        {advancedStats && (
          <>
            <StatCard icon={<LuDollarSign size={18} className="text-green-500" />} label="Total Net P&L" value={advancedStats.totalNetPnl} isPositive={performanceMetrics.total_pnl >= 0} />
            <StatCard icon={<LuSquareDivide size={18} className="text-blue-500" />} label="Profit Factor" value={advancedStats.profitFactor} />
            <StatCard icon={<LuTrendingDown size={18} className="text-red-500" />} label="Max Drawdown" value={advancedStats.maxDrawdown} isPositive={false} />
            <StatCard icon={<LuTrendingUp size={18} className="text-green-500" />} label="Average Win" value={advancedStats.avgWin} isPositive />
            <StatCard icon={<LuTrendingDown size={18} className="text-red-500" />} label="Average Loss" value={advancedStats.avgLoss} isPositive={false} />
            <StatCard icon={<LuShieldCheck size={18} className="text-indigo-500" />} label="Sharpe Ratio" value={advancedStats.sharpeRatio} />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">

        {/* Equity Curve */}
        <div className="bg-white p-3 sm:p-5 rounded-lg sm:rounded-xl shadow-sm border border-slate-200 xl:col-span-2">
          <h2 className="text-sm sm:text-lg font-bold text-slate-800 mb-2 sm:mb-4">
            Equity Curve
          </h2>
          <div className="w-full h-52 sm:h-80">
            <ResponsiveContainer>
              <LineChart data={equityCurveData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" fontSize={10} sm={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                <YAxis fontSize={10} sm={{ fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => [`₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, 'Balance']} />
                <Line type="monotone" dataKey="balance" stroke="#2563eb" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Daily P&L Heatmap */}
        <div className="bg-white p-3 sm:p-5 rounded-lg sm:rounded-xl shadow-sm border border-slate-200">
          <h2 className="text-sm sm:text-lg font-bold text-slate-800 mb-2 sm:mb-4">
            Daily P&L Heatmap
          </h2>
          <div className="overflow-x-auto">
            <CalendarHeatmap
              startDate={generatePastDate(180)}
              endDate={new Date()}
              values={dailyPnlData.map((item) => ({ date: item.date, count: item.pnl }))}
              classForValue={(value) => {
                if (!value) return 'color-empty';
                if (value.count > 2000) return 'color-scale-4';
                if (value.count > 0) return 'color-scale-3';
                if (value.count === 0) return 'color-scale-2';
                return 'color-scale-1';
              }}
              tooltipDataAttrs={(value) => ({
                'data-tip': value.date ? `${value.date}: ₹${value.count}` : 'No data',
              })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
